import { Dirent } from "node:fs";
import { readdir, stat, realpath } from "node:fs/promises";
import { homedir } from "node:os";
import path from "node:path";

const DEFAULT_LIMIT = 500;

export interface DirectoryListEntry {
  name: string;
  path: string;
  kind: "file" | "directory";
}

export interface ListDirectoryOptions {
  /**
   * Path to list. Empty string and "~" both resolve to the user's HOME.
   * Leading "~/" is expanded to HOME. Otherwise treated as absolute.
   */
  path: string;
  includeFiles?: boolean;
  includeHidden?: boolean;
  limit?: number;
}

export interface ListDirectoryResult {
  /** Resolved absolute path that was actually listed. */
  path: string;
  /** Parent directory's absolute path, or null if at FS root. */
  parent: string | null;
  entries: DirectoryListEntry[];
}

/**
 * List immediate children of a directory by absolute path. Unlike the search
 * helpers in `directory-suggestions.ts`, this is intentionally NOT scoped to
 * any root — it's the backing implementation for the project-picker browse
 * mode, which needs to reach removable media (`/Volumes`, `/media`, `/mnt`),
 * symlinked workdirs, etc.
 *
 * Hidden entries (names starting with ".") are filtered unless explicitly
 * included. Entries are sorted: directories first, then files, each group
 * case-insensitive alphabetical. Permission errors on a child entry are
 * silently dropped (we still return the directory, just minus that entry).
 *
 * Throws when the requested path itself is unreadable (caller wraps in
 * structured error response).
 */
export async function listDirectoryContents(
  options: ListDirectoryOptions,
): Promise<ListDirectoryResult> {
  const includeFiles = options.includeFiles ?? false;
  const includeHidden = options.includeHidden ?? false;
  const limit = clampLimit(options.limit);

  const resolvedPath = await resolveAbsolutePath(options.path);
  const dirents = (await readdir(resolvedPath, { withFileTypes: true })) as Dirent[];

  const collected: DirectoryListEntry[] = [];
  for (const dirent of dirents) {
    if (!includeHidden && dirent.name.startsWith(".")) continue;
    const childPath = path.join(resolvedPath, dirent.name);
    const kind = await classifyEntry(dirent, childPath);
    if (!kind) continue;
    if (kind === "file" && !includeFiles) continue;
    collected.push({ name: dirent.name, path: childPath, kind });
    if (collected.length >= limit) break;
  }

  collected.sort(compareEntries);

  const parent = path.dirname(resolvedPath);
  return {
    path: resolvedPath,
    parent: parent === resolvedPath ? null : parent,
    entries: collected,
  };
}

async function resolveAbsolutePath(rawPath: string): Promise<string> {
  const home = process.env.HOME ?? homedir();
  const trimmed = rawPath.trim();
  if (trimmed === "" || trimmed === "~") {
    return path.resolve(home);
  }
  if (trimmed.startsWith("~/")) {
    return path.resolve(home, trimmed.slice(2));
  }
  if (!path.isAbsolute(trimmed)) {
    throw new Error(
      `list_directory requires an absolute path, an empty string, or a "~"-prefixed path; got ${JSON.stringify(rawPath)}`,
    );
  }
  // Resolve symlinks so the parent navigation gives sensible results.
  try {
    return await realpath(trimmed);
  } catch {
    return path.resolve(trimmed);
  }
}

async function classifyEntry(
  dirent: Dirent,
  fullPath: string,
): Promise<"file" | "directory" | null> {
  if (dirent.isDirectory()) return "directory";
  if (dirent.isFile()) return "file";
  if (dirent.isSymbolicLink()) {
    try {
      const stats = await stat(fullPath);
      if (stats.isDirectory()) return "directory";
      if (stats.isFile()) return "file";
    } catch {
      // Broken symlink — drop it.
      return null;
    }
  }
  return null;
}

function compareEntries(a: DirectoryListEntry, b: DirectoryListEntry): number {
  if (a.kind !== b.kind) return a.kind === "directory" ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

function clampLimit(limit: number | undefined): number {
  if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0) return DEFAULT_LIMIT;
  return Math.min(Math.floor(limit), 2000);
}
